import { useAppDispatch, useAppSelector } from "../../hooks/redux";
import { TMobileRouterPath, useAppNavigate } from "../../hooks/router";
import { useEffect, useState } from "react";
import { routes } from "../../router/mobile/routes";
import {
  setLayoutState,
  setMenuOpen,
  setTopRightIcon,
} from "../../redux/Layout/layout.slice";
import { IAnalyticsController } from "../../hexagon/adapters/driver/analyticsController";
import { useCurrentPath } from "../../hooks/useCurrentPath";
import { setLocalBetaFeature } from "../../redux/Setting/setting.slice";
import { Location, useSearchParams } from "react-router-dom";
import {
  setRedirectToUserInfo,
  setUserClinic,
} from "../../redux/Clinic/clinic.slice";
import { setDashboardStateReset } from "../../redux/Dashboard/dashboard.slice";
import { ISummaryController } from "../../hexagon/adapters/driver/summaryController";
import { IlocalStorageProvider } from "../../hexagon/adapters/driven/providers/localStorageProvider";
import { isVitalsSubdomain } from "../../util/subdomain";

const tapEventTabs = [
  {
    key: routes.healthOverview.path,
    tapEvent: "Tap on Health Overview",
  },
];

export interface IUseHook {
  analyticsController: IAnalyticsController;
  location: Location;
  clinicController: IClinicController;
  populateAppData: (changeFacility?: boolean) => Promise<void>;
  summaryController: ISummaryController;
  localStorageProvider: IlocalStorageProvider;
}

export const useHook = ({
  analyticsController,
  location,
  clinicController,
  populateAppData,
  summaryController,
  localStorageProvider,
}: IUseHook) => {
  const dispatch = useAppDispatch();
  const pathname = useCurrentPath();
  const navigate = useAppNavigate();
  const [confirmLogout, setConfirmLogout] = useState<boolean>(false);

  const onLocalBetaFeature = (val: boolean) => {
    dispatch(setLocalBetaFeature(val));
  };

  const {
    user,
    userClinic,
    topRightIcon,
    showHeader,
    localBetaFeature,
    menuOpen,
    connectionStatus,
    clinicList,
    redirectToUserInfo,
  } = useAppSelector((state) => ({
    user: state.auth.user,
    userClinic: state.clinic.userClinic,
    clinicList: state.clinic.clinicList,
    topRightIcon: state.layout.topRightIcon,
    showHeader: state.layout.showHeader,
    localBetaFeature: state.settings.localBetaFeature,
    menuOpen: state.layout.menuOpen,
    connectionStatus: state.network.connectionStatus,
    redirectToUserInfo: state.clinic.redirectToUserInfo,
  }));

  const _email = user?.email || "";
  const _name = user?.fullName || "";

  useEffect(() => {
    if (redirectToUserInfo && localStorageProvider.get("sso") === "true") {
      navigate(routes.userInfo.path);
      dispatch(setRedirectToUserInfo(false));
    }
    if (topRightIcon?.type === "custom") {
      analyticsController.logEvent("Tap on add icon button in watchlist", {
        email: user?.email,
        selectedFacility: userClinic?.name,
        selectedFacilityId: userClinic?.id,
        cUserId: user?.userID,
      });
    }
  }, [redirectToUserInfo]);

  const onRightIconClick = () => {
    if (topRightIcon?.type === "custom") {
      analyticsController.logEvent("Tap on add icon button in watchlist", {
        email: user?.email,
        selectedFacility: userClinic?.name,
        selectedFacilityId: userClinic?.id,
        cUserId: user?.userID,
      });
    }
    if (topRightIcon && topRightIcon.type === "custom") {
      topRightIcon && topRightIcon.action && topRightIcon.action();
      return;
    }
    if (location.pathname === "/dashboard" && topRightIcon?.type === "filter") {
      analyticsController.logEvent("Tap on filter button in dashboard", {
        email: user?.email,
        selectedFacility: userClinic?.name,
        selectedFacilityId: userClinic?.id,
        cUserId: user?.userID,
      });
    }

    if (
      location.pathname === "/clinical" &&
      topRightIcon?.type === "calender"
    ) {
      analyticsController.logEvent("Tap on Date button in clinic", {
        email: user?.email,
        selectedFacility: userClinic?.name,
        selectedFacilityId: userClinic?.id,
        cUserId: user?.userID,
      });
    }
    if (location.pathname === "/reports" && topRightIcon?.type === "calender") {
      analyticsController.logEvent("Tap on Date button in reports", {
        email: user?.email,
        selectedFacility: userClinic?.name,
        selectedFacilityId: userClinic?.id,
        cUserId: user?.userID,
      });
    }

    topRightIcon && dispatch(setTopRightIcon({ ...topRightIcon, open: true }));
  };

  const toggleMenu = (e?: { stopPropagation: () => void }) => {
    if (e) e.stopPropagation();
    if (!menuOpen) {
      analyticsController.logEvent("Tap on setting menu", {
        email: user?.email,
        selectedFacility: userClinic?.name,
        selectedFacilityId: userClinic?.id,
        cUserId: user?.userID,
      });
    } else {
      analyticsController.logEvent("Tap on close setting menu", {
        email: user?.email,
        selectedFacility: userClinic?.name,
        selectedFacilityId: userClinic?.id,
        cUserId: user?.userID,
      });
    }
    dispatch(setMenuOpen(!menuOpen));
  };

  const onClickSearch = () => {
    analyticsController.logEvent("Global search", {
      email: user?.email,
      selectedFacility: userClinic?.name,
      selectedFacilityId: userClinic?.id,
      cUserId: user?.userID,
    });
    navigate(routes.search.path);
  };

  const showHeaderBar = () => {
    if (!showHeader) return false;
    const menuRoutes: TMobileRouterPath[] = [];
    return menuRoutes.includes(location.pathname as TMobileRouterPath);
  };

  const hideHeaderBorder = () => {
    const menuRoutes: TMobileRouterPath[] = [];
    return menuRoutes.includes(location.pathname as TMobileRouterPath);
  };

  const showFooter = () => {
    // Hide footer on vitals subdomain since users will always be redirected to vitals-ai
    if (isVitalsSubdomain()) {
      return false;
    }

    // Health app routes that show footer
    const menuRoutes: TMobileRouterPath[] = [
      routes.healthOverview.path,
      routes.healthChat.path,
      routes.healthCare.path,
      routes.healthReminders.path,
      routes.patientProfile.path,
      routes.patientProfileSharing.path,
    ];

    // AI Coach and Voice are full-screen views without footer
    if (
      pathname === routes.aiCoach.path ||
      pathname === routes.voiceAssistant.path
    ) {
      return false;
    }

    return menuRoutes.includes(pathname as TMobileRouterPath);
  };

  const onCancel = () => {
    setConfirmLogout(false);
  };

  const onLogoutClick = () => {
    setConfirmLogout(true);
  };

  const footerTapEvent = (route: string) => {
    const tab = tapEventTabs.find((item) => item.key === route);
    if (tab) {
      analyticsController.logEvent(tab.tapEvent, {
        email: user?.email,
        selectedFacility: userClinic?.name,
        selectedFacilityId: userClinic?.id,
        cUserId: user?.userID,
      });
    }
  };

  // Change facility before loading page
  const [showLoading, setShowLoading] = useState(false);

  const [searchParams] = useSearchParams();
  const clinicId = searchParams.get("clinic_id");

  const { notificationSettings } = useAppSelector((state) => ({
    notificationSettings: state.settings.notifications,
  }));

  const changeFacility = async (clinicId: number) => {
    const selectedClinic = await clinicController.setSelectedClinic({
      id: clinicId,
      clinicList,
      notificationSettings: {
        email: notificationSettings?.email || false,
        pushNotification: notificationSettings?.pushNotification || false,
        kioskMode: notificationSettings?.kioskMode || false,
        escalatedAlertPreference:
          notificationSettings?.escalatedAlertPreference || [],
        psnAlertPreference: notificationSettings?.psnAlertPreference || [],
        bedExitPreference: notificationSettings?.bedExitPreference || [],
        facilitiesToSendNotificationForOldId: [clinicId],
      },
    });
    if (selectedClinic) {
      dispatch(setUserClinic({ clinic: selectedClinic }));
      dispatch(setLayoutState({ pageTabs: {} }));
      dispatch(setDashboardStateReset());
      await populateAppData(true);
    }
  };

  useEffect(() => {
    const changeFacilityAsync = async () => {
      setShowLoading(true);
      await changeFacility(Number(clinicId));
      setShowLoading(false);
    };

    if (clinicId && Number(clinicId) !== userClinic?.id) {
      changeFacilityAsync();
    }
  }, [clinicId]);
  //

  const onPatientClick = (patientId: number) => {};
  const onSetupClick = (patientId: number) => {};

  return {
    menuOpen,
    user,
    userClinic,
    topRightIcon,
    toggleMenu,
    onClickSearch,
    showHeaderBar,
    hideHeaderBorder,
    showFooter,
    onRightIconClick,
    uniquePageKey: location.key,
    confirmLogout,
    onCancel,
    onLogoutClick,
    setConfirmLogout,
    localBetaFeature,
    onLocalBetaFeature,
    footerTapEvent,
    pathname,
    connectionStatus,
    showLoading,
    onPatientClick,
    onSetupClick,
  };
};
